import React, { useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Typography, Box } from '@material-ui/core';

import { SpringHealthEnrollmentStep } from './springHealthEnrollmentState';
import ConfirmDetails from './steps/ConfirmDetails/ConfirmDetails';
import ReadyToStart from './steps/ReadyToStart/ReadyToStart';

import { Product } from '@/utils/productHelpers';
import {
  EMAIL_VALIDATION_REGEX,
  PASSWORD_VALIDATION_REGEX,
  PHONE_VALIDATION_REGEX,
} from '@/utils/constants';

const createSchema = (t: (key: string) => string) => z.object({
  firstName: z.string()
    .min(1, { message: t('coverage.spring_health.errors.required_field') }),
  lastName: z.string()
    .min(1, { message: t('coverage.spring_health.errors.required_field') }),
  zipCode: z.string()
    .min(1, { message: t('coverage.spring_health.errors.required_field') }),
  email: z.string()
    .regex(EMAIL_VALIDATION_REGEX, { message: t('coverage.spring_health.errors.invalid_email') }),
  phoneNumber: z.union([
    z.literal(''),
    z.string().regex(PHONE_VALIDATION_REGEX, { message: t('coverage.spring_health.errors.invalid_phone') }),
  ]).optional(),
  password: z.string()
    .regex(PASSWORD_VALIDATION_REGEX, { message: t('coverage.spring_health.errors.password_rules_error') }),
  confirmPassword: z.string()
    .min(1, { message: t('coverage.spring_health.errors.required_field') }),
  acceptTerms: z.literal(true, {
    errorMap: () => ({ message: t('coverage.spring_health.errors.accept_terms_required') }),
  }),
  smsConsent: z.boolean().optional(),
  marketingConsent: z.boolean().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: t('coverage.spring_health.errors.passwords_must_match'),
  path: ['confirmPassword'],
});

export type SpringHealthFormData = z.infer<ReturnType<typeof createSchema>>;

type Props = {
  product: Product;
};

const SpringHealthEnrollment: React.FC<Props> = ({ product }) => {
  const [step, setStep] = useState<SpringHealthEnrollmentStep>('ConfirmDetails');
  const { t } = useTranslation();

  const schema = createSchema(t);

  const methods = useForm<SpringHealthFormData>({
    defaultValues: {
      firstName: '',
      lastName: '',
      zipCode: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      acceptTerms: undefined,
      smsConsent: false,
      marketingConsent: false,
    },
    mode: 'onBlur',
    resolver: zodResolver(schema),
  });

  const renderStep = () => {
    switch (step) {
    case 'ConfirmDetails':
      return (
        <ConfirmDetails
          product={product}
          onSuccess={() => setStep('ReadyToStart')}
          onError={() => setStep('Error')}
        />
      );
    case 'ReadyToStart':
      return <ReadyToStart product={product} />;
    case 'Error':
      return (
        <Box sx={{ textAlign: 'center', paddingTop: 32 }}>
          <Typography variant="h2" style={{ marginBottom: 16 }}>
            {t('coverage.spring_health.errors.generic_error')}
          </Typography>
        </Box>
      );
    default:
      return null;
    }
  };

  return (
    <FormProvider {...methods}>
      <form>
        {renderStep()}
      </form>
    </FormProvider>
  );
};

export default SpringHealthEnrollment;
